import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sprout,
  Mail,
  Lock,
  User,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { api } from "./convex/_generated/api";
import { useAction } from "convex/react";
import { toast } from "sonner";

import "./Auth2.css";

export default function Auth2() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const createUser = useAction(api.mfa.createUsers);
  const loginUser = useAction(api.mfa.loginUser);
  const sendOTP = useAction(api.mfa.sendOTP);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const user = await loginUser({
          email,
          password,
        });

        if (!user) {
          toast.error("User not found");
        } else {
          toast.success(
            `Welcome back, ${user.fullName || user.email}`
          );

          if (!user.mfaEnabled) {
            navigate("/mfa-setup", {
              state: {
                userId: user.userId,
                email: user.email,
              },
            });
          } else {
            await sendOTP({
              userId: user.userId,
              email: user.email,
            });

            navigate("/mfa-verify", {
              state: {
                userId: user.userId,
                email: user.email,
              },
            });
          }
        }
      } else {
        if (!fullName.trim()) {
          toast.error("Please enter your full name");
          return;
        }

        const user = await createUser({
          email,
          password,
          fullName,
        });

        toast.success("Account created!");

        if (!user.mfaEnabled) {
          navigate("/mfa-setup", {
            state: {
              userId: user.userId,
              email: user.email,
            },
          });
        } else {
          navigate("/mfa-verify", {
            state: {
              userId: user.userId,
              email: user.email,
            },
          });
        }
      }
    } catch (error) {
      toast.error(
        error.message || "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">

      <div className="auth-glow auth-glow--top" />
      <div className="auth-glow auth-glow--bottom" />

      {/* Header */}

      <header className="auth-header">

        <div className="auth-logo">

          <span className="auth-logo-icon">
            🌱
          </span>

          <span className="auth-logo-text">
            HabitFlow
          </span>

          <span className="auth-logo-beta">
            Becomes a Habit
          </span>

        </div>

      </header>

      <div className="auth-center">

        <div className="auth-card">

          {/* Icon */}

          <div className="auth-icon-wrap">
            <Sprout size={28} />
          </div>

          <h1 className="auth-title">
            {isLogin
              ? "Welcome back"
              : "Create your account"}
          </h1>

          <p className="auth-subtitle">
            {isLogin
              ? "Sign in to continue building better habits"
              : "Start building healthier habits, one day at a time"}
          </p>

          <form
            onSubmit={handleSubmit}
            className="auth-form"
          >

            {/* Full Name */}

            {!isLogin && (
              <div className="auth-field">

                <label className="auth-label">
                  Full Name
                </label>

                <div className="auth-input-wrap">

                  <User
                    size={16}
                    className="auth-input-icon"
                  />

                  <input
                    type="text"
                    placeholder="John Doe"
                    value={fullName}
                    onChange={(e) =>
                      setFullName(e.target.value)
                    }
                    className="auth-input"
                    required
                  />

                </div>

              </div>
            )}

            {/* Email */}

            <div className="auth-field">

              <label className="auth-label">
                Email
              </label>

              <div className="auth-input-wrap">

                <Mail
                  size={16}
                  className="auth-input-icon"
                />

                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) =>
                    setEmail(e.target.value)
                  }
                  className="auth-input"
                  required
                />

              </div>

            </div>

            {/* Password */}

            <div className="auth-field">

              <label className="auth-label">
                Password
              </label>

              <div className="auth-input-wrap">

                <Lock
                  size={16}
                  className="auth-input-icon"
                />

                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) =>
                    setPassword(e.target.value)
                  }
                  className="auth-input"
                  required
                  minLength={8}
                />

              </div>

            </div>

            {/* Submit */}

            <button
              type="submit"
              disabled={loading}
              className={`auth-submit-button ${
                loading
                  ? "auth-submit-button--loading"
                  : ""
              }`}
            >
              {loading ? (
                <Loader2
                  size={18}
                  className="auth-spinner"
                />
              ) : (
                <>
                  {isLogin
                    ? "Sign In"
                    : "Create Account"}

                  <ArrowRight size={18} />
                </>
              )}
            </button>

          </form>

          {/* Toggle */}

          <div className="auth-toggle">

            <button
              type="button"
              onClick={() =>
                setIsLogin(!isLogin)
              }
              className="auth-toggle-button"
            >
              {isLogin
                ? "Don't have an account? Sign up"
                : "Already have an account? Sign in"}
            </button>

          </div>

        </div>

      </div>

    </div>
  );
}
